import dynamic from "next/dynamic";
import React, { useState } from "react";
import { V3IconClose } from "../../components/Icons";
import { MarketingAllBenefits } from "../../components/MarketingAllBenefits";
import { DotsFeatures2, FeaturesSlides2, MarketingCarousel, UseCaseFeatures2, UseCasesSlides2 } from "../../components/MarketingCarousel";
import { CTASize, CTAVariant, MarketingCTA } from "../../components/MarketingCTA";
import { MarketingFooter } from "../../components/MarketingFooter";
import { MarketingG2Banner } from "../../components/MarketingG2Banner";
import { MarketingGetStarted } from "../../components/MarketingGetStarted";
import { MarketingMenu } from "../../components/MarketingMenu";
import { MarketingOGTags } from "../../components/MarketingOGTags";
import { MarketingTextBlock } from "../../components/MarketingTextBlock";
import { MarketingTextSVGHeader } from "../../components/MarketingTextSVGHeader";
import { MarketingUserPledge2 } from "../../components/MarketingUserPledge2";
import { FadeUpContainer } from "../../components/ReusableStyledComponents/FadeUp";
import { MarketingTitleBr } from "../../components/ReusableStyledComponents/MarketingMainTitle";
import { UnstyledLink } from "../../components/ReusableStyledComponents/UnstyledLink";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import { theme } from "../../theme/theme";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import {
    HMModal,
    HMModalIconClosePos,
    HPMainBtnWrapper,
    HPMainFloatWrapper,
    HPVideoIframe,
    MarketingHiddenMobileblock,
    MarketingHomepageImageVariant,
    YoutubeSizer,
    YTHPIframe
} from "./PortfolioPageLayout.styles";

const MarketingCarouselUser = dynamic(() => import("../../components/MarketingCarouselUser/MarketingCarouselUser"), { ssr: false });

const YTHPVid = ({ autoplay }: { autoplay?: boolean }) => (
    <HPVideoIframe>
        <YTHPIframe
            src={`https://www.youtube.com/embed/S_rJ5oP2XMI?cc_load_policy=1${autoplay ? "&autoplay=1" : ""}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder="0"
        />
    </HPVideoIframe>
);

const modalOverlay = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
        backgroundColor: "rgba(0, 0, 0, 0.75)"
    }
};

type PortfolioPageLayoutProps = {
    customTitle?: JSX.Element;
    userVariant?: boolean;
};

export const PortfolioPageLayout = ({ customTitle, userVariant = false }: PortfolioPageLayoutProps) => {
    const [modalIsOpen, setIsOpen] = useState(false);

    return (
        <>
            <MarketingOGTags
                title="Authory - Save & Showcase Everything You've Written and Created"
                description="Try the done-for-you portfolio builder that automatically backs up your bylines, podcasts, videos, and more. Test it for free."
                canonicalUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}`}
            />
            <MarketingMenu />
            <FadeUpContainer>
                <VerticalSpacing top={20} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 50 }]}>
                    <MarketingTextSVGHeader
                        customTitleMarginBottom={20}
                        textMarginTop={30}
                        title={
                            <>
                                {customTitle ? (
                                    customTitle
                                ) : (
                                    <>
                                        <strong>
                                            Create A Beautiful Portfolio &<MarketingTitleBr /> Automatically Back Up Your Work
                                        </strong>
                                    </>
                                )}
                            </>
                        }
                        description={
                            userVariant ? (
                                <>
                                    Authory finds all the articles, podcasts and videos you've ever created, and turns them into a beautiful portfolio
                                    page for you. Plus: Your portfolio is auto-updating whenever you publish something new, comes with automated
                                    content backups, social media analytics & much more.
                                </>
                            ) : (
                                <>
                                    Authory aggregates everything you've ever written or recorded and generates a beautiful portfolio page so you can
                                    showcase, share, and save your life's work.
                                </>
                            )
                        }
                        desktopSVG={
                            <svg width="1351" height="224" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M75 168a4 4 0 0 1-4-4v-40a4 4 0 0 1 4-4h44v44a4 4 0 0 1-4 4H75Z" fill="#82AEFF" />
                                <path d="M119.5 79.5H.5V4A3.5 3.5 0 0 1 4 .5h115.5v79Z" stroke="#E4EDFF" />
                                <path
                                    d="M179 80.5h-59.5V140a3.5 3.5 0 0 0 3.5 3.5h59.5V84a3.5 3.5 0 0 0-3.5-3.5Z"
                                    stroke="#E4EDFF"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="m142.782 91.287-5.254 3.227a1.946 1.946 0 0 0-.64 2.674l18.167 29.677c.226.369.549.669.935.868l6.037 3.115a1.354 1.354 0 0 0 1.973-1.213l-.034-6.803a2.376 2.376 0 0 0-.349-1.228L145.45 91.927a1.939 1.939 0 0 0-2.668-.64Z"
                                    fill="#E4EDFF"
                                />
                                <path d="M1239 176h48v44a4 4 0 0 1-4 4h-40a4 4 0 0 1-4-4v-44Z" fill="#FFA858" />
                                <path
                                    d="M1287.5 72.5h63V172c0 1.933-1.57 3.5-3.5 3.5h-59.5v-103ZM1195 128.5h40c1.93 0 3.5 1.567 3.5 3.5v43.5H1195c-1.93 0-3.5-1.567-3.5-3.5v-40c0-1.933 1.57-3.5 3.5-3.5Z"
                                    stroke="#E4EDFF"
                                />
                            </svg>
                        }
                        mobileSVG={<></>}
                        textMaxWidth={1200}
                        containerMaxWidth={1320}
                        mobileMaxWidth={344}
                        descriptionTextMaxWidth={userVariant ? 820 : 700}
                        floatingComponent={
                            <HPMainFloatWrapper userVariant={userVariant}>
                                <HPMainBtnWrapper>
                                    <div>
                                        <UnstyledLink href={APPLICATION_ROUTES.SIGNUP}>
                                            <MarketingCTA text="Create your portfolio in 5 minutes" size={CTASize.L} />
                                        </UnstyledLink>
                                    </div>
                                    {userVariant && (
                                        <div>
                                            <MarketingCTA
                                                text="Watch video"
                                                size={CTASize.L}
                                                variant={CTAVariant.Secundary}
                                                minWidth={277}
                                                onClick={() => setIsOpen(true)}
                                            />
                                        </div>
                                    )}
                                </HPMainBtnWrapper>
                                {!userVariant && (
                                    <VerticalSpacing top={-33}>
                                        <MarketingAllBenefits useBlockVariant />
                                    </VerticalSpacing>
                                )}
                            </HPMainFloatWrapper>
                        }
                    />
                </VerticalSpacing>
                {!userVariant ? (
                    <>
                        <VerticalSpacing top={45} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 150 }]}>
                            <MarketingG2Banner />
                        </VerticalSpacing>
                        <VerticalSpacing top={80}>
                            <YoutubeSizer>
                                <YTHPVid />
                            </YoutubeSizer>
                        </VerticalSpacing>
                    </>
                ) : (
                    <VerticalSpacing top={45} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 200 }]}>
                        <MarketingHomepageImageVariant src="/authory_static_files/images/homepage/annalisa_profile.png" />
                    </VerticalSpacing>
                )}
                <VerticalSpacing top={-100} breakpoints={[{ screen: theme.breakpoints.phone, marginTop: userVariant ? -240 : -200 }]}>
                    <MarketingUserPledge2 useBkg={!userVariant} />
                </VerticalSpacing>
                <VerticalSpacing top={80} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 100 }]}>
                    <MarketingTextBlock
                        title={
                            <>
                                <strong>What you can do</strong> with Authory
                            </>
                        }
                        description={
                            <>
                                Authory will find and import everything you've ever created, and then{" "}
                                <strong>save you many hours of manual work each month</strong> automating all the tasks that are important after your
                                content has been published.
                            </>
                        }
                        backgroundColor="white"
                        useDash
                        descriptionMaxWidth={618}
                        noPadding
                    />
                </VerticalSpacing>
                <VerticalSpacing top={85}>
                    <MarketingCarousel
                        dotComponents={DotsFeatures2}
                        slidesComponents={FeaturesSlides2}
                        effect="fade"
                        paginationWidth={1050}
                        carouselMaxWidth={1200}
                        shrinkLength={150}
                        lastElementWidth={85}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={85}>
                    <MarketingCarouselUser />
                </VerticalSpacing>
                <MarketingHiddenMobileblock>
                    <VerticalSpacing top={80} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 100 }]}>
                        <MarketingTextBlock
                            title={
                                <>
                                    Made for extraordinary content creators. <strong>Made for you!</strong>
                                </>
                            }
                            description={
                                <>
                                    Authory is the perfect solution to <strong>automate many of the chores that come with creating content,</strong>{" "}
                                    leaving you with more time for the work you love.
                                </>
                            }
                            backgroundColor="white"
                            useDash
                            descriptionMaxWidth={660}
                            noPadding
                        />
                    </VerticalSpacing>
                    <VerticalSpacing top={85}>
                        <MarketingCarousel
                            dotComponents={UseCaseFeatures2}
                            slidesComponents={UseCasesSlides2}
                            paginationWidth={1000}
                            carouselMaxWidth={1200}
                            effect="fade"
                            shrinkLength={280}
                            lastElementWidth={140}
                        />
                    </VerticalSpacing>
                </MarketingHiddenMobileblock>
                <VerticalSpacing top={120}>
                    <MarketingGetStarted />
                </VerticalSpacing>
                <VerticalSpacing>
                    <MarketingFooter />
                </VerticalSpacing>
            </FadeUpContainer>
            {modalIsOpen && (
                <HMModal isOpen={modalIsOpen} onRequestClose={() => setIsOpen(false)} style={modalOverlay as ReactModal.Styles} ariaHideApp={false}>
                    <HMModalIconClosePos>
                        <V3IconClose onClick={() => setIsOpen(false)} />
                    </HMModalIconClosePos>
                    <YTHPVid autoplay />
                </HMModal>
            )}
        </>
    );
};
